import { Component, OnDestroy, OnInit } from '@angular/core';
import { UntypedFormGroup, FormControl, Validators } from '@angular/forms';
import { MatChipInputEvent } from '@angular/material/chips';
import { MatDialog } from '@angular/material/dialog';
import { MatRadioChange } from '@angular/material/radio';
import { ActivatedRoute } from '@angular/router';
import { Subscription } from 'rxjs';
import { ManagementArea } from 'src/app/data-models/area-models/management-area.model';
import { SpeciesData } from 'src/app/data-models/catch-table-models/species-data.model';
import { Coordinate } from 'src/app/data-models/coordinate.model';
import { LostRecoveredGearForm } from 'src/app/data-models/report-models/lost-recovered-found-gear.model';
import { Trip } from 'src/app/data-models/trip-models/trip.model';
import { UserData } from 'src/app/data-models/user-models/user-data.model';
import { ConfirmationDialogComponent } from 'src/app/dialogs/confirmation-dialog/confirmation-dialog.component';
import { AuthenticationService } from 'src/app/services/authentication.service';
import { SharedMethodsService } from 'src/app/services/shared-methods.service';
import { CommonDataService } from 'src/app/services/storage/common-data.service';
import { UserStorageService } from 'src/app/services/storage/user-storage.service';
import { coordValidator, latitudeValidator, longitudeValidator } from 'src/app/data-models/validator.model';
import { GeolocationMethodsService } from 'src/app/services/geolocation-methods.service';

@Component({
    selector: 'app-lost-recovered-gear-form',
    templateUrl: './lost-recovered-gear-form.component.html',
    styleUrls: ['./lost-recovered-gear-form.component.css'],
    standalone: false
})
export class LostRecoveredGearFormComponent implements OnInit, OnDestroy {

  isReportSending: boolean = false; // flag that tells us if the report is sending (so we can show the loading screen)
  todayDate: Date = new Date();

  gearReport!: LostRecoveredGearForm;
  tripReportIndex!: number; //Index of report in the current trip
  reportStatus: string = '';

  // data preambles
  currTrip: Trip = new Trip();
  lastTrip: Trip = new Trip();

  userDetails: UserData = new UserData();


  gearForm: UntypedFormGroup = new UntypedFormGroup({
    gearStatus: new FormControl<string>('', Validators.required),
    gearType: new FormControl<string>('', Validators.required),
    gearDescription: new FormControl<string>('', Validators.required),
    gearNumber: new FormControl<number>(null!, [Validators.required, Validators.max(9999)]),
    gearTags: new FormControl<string[]>([], Validators.required),
    licenseNumber: new FormControl<string>('', Validators.required),
    targetSpecies: new FormControl<SpeciesData>(null!, Validators.required),
    netLength: new FormControl<number>(null!, [Validators.required, Validators.max(99999)]),
    buoyNumber: new FormControl<number>(null!, [Validators.required, Validators.min(0),  Validators.max(1500)]),
    ropeLength: new FormControl<number>(null!, [Validators.required, Validators.max(150000)]),
    buoyDescription: new FormControl<string>('', Validators.required),
    vesselDirection: new FormControl<string>(''),
    fisheryArea: new FormControl<string>('', Validators.required),
    latitude: new FormControl<Coordinate>(new Coordinate, [coordValidator(), latitudeValidator()]),
    longitude: new FormControl<Coordinate>(new Coordinate, [coordValidator(), longitudeValidator()]),
    comments: new FormControl<string>('')
  });

  formUpdateSubscription!: Subscription;

  declarationDate: Date = new Date();
  gearLostDate: Date = new Date();
  gearRecoveredDate: Date = new Date();
  gearTagList: string[] = [];

  // page to page
  numPages: number = 0;
  currPage: number = 0;
  spinnerProgress: number = 0;
  spinnerIncrement: number = 0;


  //Reference table values (placeholder until all ref table values properly synced down onto app)
  gearTypeOptions: string[] = ['Gillnet', 'Longline', 'Troller lines', 'Pot/Trap', 'Trawls', 'Seine nets', 'Dredges', 'Other'];
  targetedSpeciesOptions: SpeciesData[] = [new SpeciesData('Salmon, Chinook'), new SpeciesData('Salmon, Chum'), new SpeciesData('Salmon, Coho'), new SpeciesData('Salmon, Pink'), new SpeciesData('Salmon, Sockeye')];
  vesselDirectionOptions: string[] = ['North', 'North-Northeast', 'Northeast', 'East-Northeast', 'East', 'East-Southeast', 'Southeast', 'South-Southeast', 'South', 'South-Southwest', 'Southwest', 'West-Southwest', 'West', 'West-Northwest', 'Northwest', 'North-Northwest'];
  managementAreas: ManagementArea[] = [];

  constructor(
    private authenticationService: AuthenticationService,
    private route: ActivatedRoute,
    private dialog: MatDialog,
    public commonDataService: CommonDataService,
    public sharedMethodsService: SharedMethodsService,
    public geolocationMethod: GeolocationMethodsService
  ) { }


  ngOnInit() {
    let requiredData: string[] = ['management_areas_list'];
    this.authenticationService.getUserDetails().then(() => {
      this.userDetails = UserStorageService.currUser;
      UserStorageService.getCurrTrip().then(() => {
        if (this.authenticationService.dataError(UserStorageService.error)) {
          return;
        }
        this.currTrip = UserStorageService.currTrip;
        let reportId = <string>this.route.snapshot.paramMap.get('id');
        this.tripReportIndex = this.currTrip.lostRecoveredFoundGearReports.findIndex((report: LostRecoveredGearForm) => report.internalId == reportId);
        this.gearReport = this.currTrip.lostRecoveredFoundGearReports[this.tripReportIndex];
        this.reportStatus = this.route.snapshot.queryParams.status;
        this.lastTrip = UserStorageService.lastTrip;
        this.commonDataService.getSpecifiedDocs(this.userDetails.userEmail, requiredData).then(() => this.initialize());
      });
    });
  }


  initialize(): void {
    if (this.gearReport.gearStatus == "1") {
      this.numPages = 2;
      this.spinnerIncrement = 50;
    } else if (this.gearReport.gearStatus == '2') {
      this.numPages = 6;
      this.spinnerIncrement = 100 / 6;
    } else {
      this.numPages = 7;
      this.spinnerIncrement = 100 / 7;
    }
    
    if (this.reportStatus == 'complete') {
      this.patchForm();
      this.currPage = 8;
      return;
    }

    this.managementAreas = this.sharedMethodsService.getManagementAreas(this.currTrip.licenseArea.licenseName, CommonDataService.managementAreas);

    if (this.reportStatus == 'new') {
      //Set defaults???
    } else {
      //Patch form with saved values
      this.patchForm();
    }

    this.subscribeToFormChanges();
    this.subscribeToGearTypeChanges();

    this.toNextPage();

  }

  //Populates the form controls with values from the lost/recovered gear report
  patchForm() {
    this.gearForm.patchValue({
      gearStatus: this.gearReport.gearStatus,
      gearType: this.gearReport.gearID,
      gearDescription: this.gearReport.gearDescription,
      gearNumber: this.gearReport.numberOfGears,
      gearTags: this.gearReport.tagList,
      licenseNumber: this.gearReport.licenseNumber,
      targetSpecies: this.gearReport.targetSpecies,
      netLength: this.gearReport.netLength,
      buoyNumber: this.gearReport.numberOfBuoys,
      ropeLength: this.gearReport.ropeLength,
      buoyDescription: this.gearReport.buoyDescription,
      vesselDirection: this.gearReport.vesselDirection,
      fisheryArea: this.gearReport.managementArea,
      latitude: this.gearReport.latitude,
      longitude: this.gearReport.longitude,
      comments: this.gearReport.comments
    });

    this.declarationDate = this.gearReport.declarationDate;
    this.gearRecoveredDate = this.gearReport.gearRecoveredDate;
    this.gearLostDate = this.gearReport.gearLostDate;

  }


  // Subscribe to all changes in the form to update and save as changes occur
  subscribeToFormChanges() {

    this.formUpdateSubscription = this.gearForm.valueChanges.subscribe(form => {
      this.gearReport.gearStatus = form.gearStatus
      this.gearReport.gearID = form.gearType
      this.gearReport.gearDescription = form.gearDescription
      this.gearReport.numberOfGears = form.gearNumber
      this.gearReport.tagList = form.gearTags
      this.gearReport.licenseNumber = form.licenseNumber
      this.gearReport.targetSpecies = form.targetSpecies
      this.gearReport.netLength = form.netLength
      this.gearReport.numberOfBuoys = form.buoyNumber
      this.gearReport.ropeLength = form.ropeLength
      this.gearReport.buoyDescription = form.buoyDescription
      this.gearReport.vesselDirection = form.vesselDirection
      this.gearReport.managementArea = form.fisheryArea
      this.gearReport.latitude = form.latitude;
      this.gearReport.longitude = form.longitude;
      this.gearReport.comments = form.comments

      UserStorageService.saveTrip(this.currTrip, true, false);
    });
  }

  private subscribeToGearTypeChanges(): void {

    const gearTypeControl = this.gearForm.get('gearType');
    const netLengthControl = this.gearForm.get('netLength');
    const buoyNumberControl = this.gearForm.get('buoyNumber');

    if (!gearTypeControl || !netLengthControl || !buoyNumberControl) {
      return; // Return early if controls are null
    }

    gearTypeControl.valueChanges.subscribe((gearType) => {
      if (['Gillnet', 'Trawls', 'Seine nets', 'Dredges', 'Other gear'].includes(gearType)) {
        netLengthControl.setValidators([Validators.required, Validators.max(99999)]);
      } else {
        netLengthControl.clearValidators();
      }
      if (['Gillnet', 'Longline', 'Pot/Trap', 'Other gear'].includes(gearType)) {
        buoyNumberControl.setValidators([Validators.required, Validators.max(1500)]);
      } else {
        buoyNumberControl.clearValidators();
      }

      netLengthControl.updateValueAndValidity({ emitEvent: false });
      buoyNumberControl.updateValueAndValidity({ emitEvent: false });

    });
  }


  dateChange() {
    this.gearReport.declarationDate = this.declarationDate;
    this.gearReport.gearLostDate = this.gearLostDate;
    this.gearReport.gearRecoveredDate = this.gearRecoveredDate;
    UserStorageService.saveTrip(this.currTrip, true, false);
  }


  private setOrClearValidator(controlName: string, action: 'set' | 'clear'): void {

    const control = this.gearForm.get(controlName);

    if (!control) {
      return; // Return early if control is null
    }

    if (action === 'set') {
      control.setValidators([Validators.required]);
    } else if (action === 'clear') {
      control.clearValidators();
    }

    control.updateValueAndValidity({ emitEvent: false });

  }



  // clear validators if no gear lost
  private clearValidatorsForNoGearLost(): void {

    const controlsToClearValidators = [
      'gearType', 'gearDescription', 'gearNumber', 'gearTags', 'licenseNumber',
      'targetSpecies', 'netLength', 'buoyNumber', 'ropeLength', 'buoyDescription',
      'fisheryArea', 'latitude', 'longitude'
    ];

    controlsToClearValidators.forEach(controlName => {
      const control = this.gearForm.get(controlName);
      control?.clearValidators();
      control?.updateValueAndValidity({ emitEvent: false });
    });

  }


  // set required validators if gear is lost/found
  private setValidatorsForGearLost(): void {

    const controlsToSetValidators = [
      'gearType', 'gearDescription', 'gearNumber', 'gearTags', 'licenseNumber',
      'targetSpecies', 'netLength', 'buoyNumber', 'ropeLength', 'buoyDescription', 
      'fisheryArea', 'latitude', 'longitude'
    ];

    controlsToSetValidators.forEach(controlName => {

      const control = this.gearForm.get(controlName);

      switch (controlName) {
        case 'gearNumber':
          control?.setValidators([Validators.required, Validators.max(9999)]);
          break;

        case 'buoyNumber':
          control?.setValidators([Validators.required, Validators.max(1500)]);
          break;

        case 'netLength':
          control?.setValidators([Validators.required, Validators.max(99999)]);
          break;

        case 'ropeLength':
          control?.setValidators([Validators.required, Validators.max(150000)]);
          break;

        case 'latitude':
          control?.setValidators([coordValidator(), latitudeValidator()]);
          break;

        case 'longitude':
          control?.setValidators([coordValidator(), longitudeValidator()]);
          break;

        default:
          // For other controls, just set the required validator
          control?.setValidators([Validators.required]);
          break;
      }

      control?.updateValueAndValidity({ emitEvent: false });
    });

  }



  gearStatusChanged(change: MatRadioChange) {
    //No gear lost, block most elements for entry
    if (change.value == "1") {
      this.numPages = 2;
      this.spinnerIncrement = 50;
      this.spinnerProgress = 50;
      this.clearValidatorsForNoGearLost();
    } else if (change.value == '2') {
      this.numPages = 6;
      this.spinnerIncrement = 100 / 6;
      this.spinnerProgress = this.spinnerIncrement;
      this.setValidatorsForGearLost();
      this.setOrClearValidator('fisheryArea', 'set');
    } else {
      this.numPages = 7;
      this.spinnerIncrement = 100 / 7;
      this.spinnerProgress = this.spinnerIncrement;
      this.setValidatorsForGearLost();
      this.setOrClearValidator('fisheryArea', 'clear');
    }
  }

  removeTag(tag: string) {
    const index = this.gearTagList.indexOf(tag);
    if (index >= 0) {
      this.gearTagList.splice(index, 1);
      this.gearForm.get('gearTags')?.setValue(this.gearTagList);
    }
  }

  addTag(event: MatChipInputEvent): void {
    const value = (event.value || '').trim();

    if (value) {
      this.gearTagList.push(value);
      this.gearForm.get('gearTags')?.setValue(this.gearTagList);
    }

    // Clear the input value
    event.chipInput!.clear();
  }


  compareGearTypes(t1: string, t2: string) {
    return t1 === t2;
  }


  compareSpeciesData(s1: SpeciesData, s2: SpeciesData) {
    return s1 && s2 && s1.speciesName == s2.speciesName
  }

  compareArea(a1: ManagementArea, a2: ManagementArea): boolean {
    return a1 && a2 ? a1.name == a2.name && a1.ID === a2.ID : a1 === a2;
  }


  /* ---------- Page Navigation Methods ---------- */


  // check current page before going to the next one
  toNextPage(): void {
    if (this.currPage > this.numPages) {
      this.submitReport();
      return
    }

    this.currPage++;
    this.spinnerProgress += this.spinnerIncrement;
  }


  //Placeholder
  submitReport(): void {
    if (this.gearForm.valid) {
      this.isReportSending = true;
      this.gearReport.isSubmitted = true;
      this.attemptTransmission(); // attempt to send the report to DFO
    } else {
      this.sharedMethodsService.toastInvalidForm();
    }
  }


  attemptTransmission() {
    UserStorageService.saveTrip(this.currTrip).then(() => {
      // navigate to the home page
      this.sharedMethodsService.toHome(this.userDetails.currHomepage);
      this.sharedMethodsService.toastSavedSuccessfully();
      this.isReportSending = false;
      console.log(this.currTrip)
    });
  }

  // back one page
  toPreviousPage(): void {
    if (this.reportStatus === 'complete' || this.currPage == 1) this.sharedMethodsService.toHome(this.userDetails.currHomepage);
    this.currPage--;
    this.spinnerProgress -= this.spinnerIncrement;

  }


  deleteReport() {
    this.dialog.open(ConfirmationDialogComponent, {
      data: { screen: 'delete_report' }
    }).afterClosed().subscribe(result => {
      if (result) {
        UserStorageService.deleteTripReport((trip) => trip.lostRecoveredFoundGearReports, this.gearReport.internalId)
          .then(() => this.sharedMethodsService.toHome(this.userDetails.currHomepage));
      }
    });
  }


  ngOnDestroy(): void {
    if (this.formUpdateSubscription) this.formUpdateSubscription.unsubscribe();
  }

}
